// src/components/HeaderCustomerSelector.js
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { FaChevronDown, FaTimes, FaUser, FaSearch } from 'react-icons/fa';
import { useCustomerContext } from '../context/CustomerContext';
import { debounce } from 'lodash';

const HeaderCustomerSelector = () => {
  const {
    selectedCustomer,
    error,
    isCustomerUser,
    selectCustomer,
    clearCustomer,
    searchCustomers
  } = useCustomerContext();

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Server-side search function
  const performSearch = useCallback(async (term) => {
    if (!searchCustomers || !term.trim() || term.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);
      console.log('[HeaderCustomerSelector] Searching customers with term:', term);
      
      const results = await searchCustomers(term);
      console.log('[HeaderCustomerSelector] Search results:', results.length, 'customers found');
      setSearchResults(results);
    } catch (error) {
      console.error('[HeaderCustomerSelector] Search error:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [searchCustomers]);

  // Create debounced search function
  const debouncedSearch = useMemo(
    () => debounce(performSearch, 250),
    [performSearch]
  );

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSearch?.cancel();
    };
  }, [debouncedSearch]);

  // Clear search results when closing dropdown
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setSearchResults([]);
    }
  }, [isOpen]);

  // Don't show selector for customer users
  if (isCustomerUser) return null;

  // Handle search input changes
  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    
    if (term.length >= 2) {
      debouncedSearch(term);
    } else {
      setSearchResults([]);
    }
  };

  const handleCustomerSelect = (customer) => {
    selectCustomer(customer);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClearCustomer = (e) => {
    e.stopPropagation();
    clearCustomer();
    setSearchTerm('');
    setSearchResults([]);
    setIsOpen(false);
    // Focus the search input after clearing
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Customer Selector with integrated search */}
      {selectedCustomer ? (
        // Show selected customer
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-3 py-2 rounded-md border bg-blue-50 border-blue-200 text-blue-800 hover:bg-blue-100 transition-colors duration-200 w-full min-w-[200px] text-sm"
        >
          <FaUser className="w-4 h-4 flex-shrink-0" />
          
          <div className="flex-1 text-left">
            <div className="text-xs text-blue-600 font-medium">Sell to:</div>
            <div className="text-sm font-semibold truncate">{selectedCustomer.name}</div>
          </div>

          <div className="flex items-center gap-1">
            <div
              onClick={handleClearCustomer}
              className="p-1 hover:bg-blue-200 rounded transition-colors duration-150 cursor-pointer"
              title="Clear selection"
            >
              <FaTimes className="w-3 h-3" />
            </div>
            <FaChevronDown className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        </button>
      ) : (
        // Show search input when no customer selected
        <div className="flex items-center gap-2 px-3 py-2 rounded-md border bg-white border-gray-300 w-full min-w-[200px] text-sm">
          <FaSearch className="w-4 h-4 flex-shrink-0 text-gray-400" />
          
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search customers..."
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={() => setIsOpen(true)}
            className="flex-1 outline-none text-sm placeholder-gray-500"
          />
          
          {searchLoading && (
            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"></div>
          )}
          
          <FaChevronDown className={`w-3 h-3 transition-transform duration-200 text-gray-400 ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 max-h-80 overflow-hidden">
          {/* Customer List */}
          <div className="max-h-60 overflow-y-auto">
            {searchLoading ? (
              <div className="p-3 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
                <div className="text-gray-500 mt-2 text-sm">Searching customers...</div>
              </div>
            ) : error ? (
              <div className="p-3 text-red-600 text-sm">
                {error}
              </div>
            ) : searchResults.length === 0 ? (
              <div className="p-3 text-gray-500 text-sm">
                {searchTerm.trim() && searchTerm.length >= 2 ? 'No customers found' : 'Type at least 2 characters to search customers'}
              </div>
            ) : (
              searchResults.map((customer) => (
                <button
                  key={customer.id}
                  onClick={() => handleCustomerSelect(customer)}
                  className={`w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors duration-150 ${
                    selectedCustomer?.id === customer.id ? 'bg-blue-50 text-blue-800' : 'text-gray-700'
                  }`}
                >
                  <div className="font-medium text-sm">{customer.name}</div>
                  {customer.email && (
                    <div className="text-xs text-gray-500">{customer.email}</div>
                  )}
                  {customer.location && (
                    <div className="text-xs text-gray-400">{customer.location}</div>
                  )}
                </button>
              ))
            )}
          </div>

          {/* Clear Selection Option */}
          {selectedCustomer && (
            <div className="border-t border-gray-100">
              <button
                onClick={() => {
                  clearCustomer();
                  setIsOpen(false);
                  setSearchTerm('');
                }}
                className="w-full px-3 py-2 text-left text-gray-500 hover:bg-gray-50 transition-colors duration-150 text-sm"
              >
                <FaTimes className="inline w-3 h-3 mr-2" />
                Clear selection
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HeaderCustomerSelector;